import { styled } from '@mui/material/styles'
import clsx from 'clsx'
import React from 'react'
import {
    ApplyBand,
    buttonSx,
    classToFComponent,
    CollapsedPaneFilterIcon,
    CollapsedPaneHeader,
    CollapsedPaneLabel,
    FCClassContext,
    Field,
    FieldHelp,
    FieldLabel,
    FilterPaneContainer,
    IconActionButton,
    inputSx,
    PaneTitle,
    SwitchRow,
    switchSx
} from 'src/utils/views'
import { makeStyles } from 'tss-react/mui'
import { Logger } from 'wdc-cube'

import CleaningServicesIcon from '@mui/icons-material/CleaningServices'
import FilterListIcon from '@mui/icons-material/FilterList'
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft'
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight'

import Autocomplete from '@mui/material/Autocomplete'
import Button from '@mui/material/Button'
import MenuItem from '@mui/material/MenuItem'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'

import { formatMonthLabel } from 'src/utils/formatter-utils'

import { BillingFilterPaneScope } from '../bl_scopes'

const LOG = Logger.get('BL-FILTER-VIEW')

const HeaderContainer = styled('div')({
    display: 'flex',
    flexDirection: 'row',
    alignSelf: 'stretch',
    alignItems: 'center',
    gap: 8,
    padding: '14px 12px 14px 18px',
    borderBottom: '1px solid var(--border)',
    boxSizing: 'border-box',
    flexShrink: 0,
    '& .MuiSvgIcon-root': { fontSize: 20 }
})

const FilterBody = styled('div')({
    padding: '18px 18px 4px',
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    flexGrow: 1,
    minHeight: 0,
    overflowY: 'auto'
})

const useStyles = makeStyles()({
    expanded: {
        width: '384px'
    },
    shrinked: {
        minWidth: 44
    }
})

type GroupOption = { id: number; name: string }

type FilterPaneProps = {
    className?: string
    scope: BillingFilterPaneScope
}

class FilterPaneViewClass implements FCClassContext<FilterPaneProps> {
    scope!: BillingFilterPaneScope
    classes!: ReturnType<typeof useStyles>['classes']

    readonly emitToggleExpanded = () => this.scope.onToggleExpanded().catch(LOG.caught)
    readonly emitClear = () => this.scope.onClear().catch(LOG.caught)
    readonly emitApply = () => this.scope.onApply().catch(LOG.caught)

    readonly emitStartMonthChanged = (e: React.ChangeEvent<HTMLInputElement>) =>
        this.scope.onStartMonthChanged(e.target.value).catch(LOG.caught)

    readonly emitEndMonthChanged = (e: React.ChangeEvent<HTMLInputElement>) =>
        this.scope.onEndMonthChanged(e.target.value).catch(LOG.caught)

    readonly emitMonthChanged = (e: React.ChangeEvent<HTMLInputElement>) =>
        this.scope.onMonthChanged(e.target.value || undefined).catch(LOG.caught)

    readonly emitOnlyExceededChanged = (_event: unknown, checked: boolean) =>
        this.scope.onOnlyExceededChanged(checked).catch(LOG.caught)

    readonly emitGroupChanged = (_event: unknown, option: GroupOption | null) =>
        this.scope.onGroupChanged(option?.id).catch(LOG.caught)

    render({ className, scope }: FilterPaneProps) {
        this.scope = scope
        this.classes = useStyles().classes

        return (
            <FilterPaneContainer
                className={clsx(scope.expanded ? this.classes.expanded : this.classes.shrinked, className)}
            >
                {scope.expanded ? this.renderOpenFilter() : this.renderCloseFilter()}
            </FilterPaneContainer>
        )
    }

    renderOpenFilter() {
        const { scope } = this

        return (
            <>
                <HeaderContainer>
                    <FilterListIcon />
                    <PaneTitle>Filtros</PaneTitle>
                    <Tooltip title="Ocultar filtros">
                        <IconActionButton size="small" aria-label="Ocultar filtros" onClick={this.emitToggleExpanded}>
                            <KeyboardDoubleArrowRightIcon />
                        </IconActionButton>
                    </Tooltip>
                </HeaderContainer>
                <FilterBody>
                    <Field>
                        <FieldLabel htmlFor="blStartMonth">Competência inicial</FieldLabel>
                        <TextField
                            id="blStartMonth"
                            type="month"
                            size="small"
                            value={scope.startMonth}
                            onChange={this.emitStartMonthChanged}
                            sx={inputSx}
                            fullWidth
                        />
                    </Field>
                    <Field>
                        <FieldLabel htmlFor="blEndMonth">Competência final</FieldLabel>
                        <TextField
                            id="blEndMonth"
                            type="month"
                            size="small"
                            value={scope.endMonth}
                            onChange={this.emitEndMonthChanged}
                            sx={inputSx}
                            fullWidth
                        />
                    </Field>
                    <Field>
                        <FieldLabel htmlFor="blMonth">Competência selecionada</FieldLabel>
                        <TextField
                            id="blMonth"
                            select
                            size="small"
                            value={scope.monthOptions.includes(scope.month ?? '') ? scope.month : ''}
                            onChange={this.emitMonthChanged}
                            sx={inputSx}
                            fullWidth
                        >
                            <MenuItem value="">Todas as competências</MenuItem>
                            {scope.monthOptions.map((month) => (
                                <MenuItem key={month} value={month}>
                                    {formatMonthLabel(month)}
                                </MenuItem>
                            ))}
                        </TextField>
                        <FieldHelp>O recorte vale na hora, sem passar por “Aplicar”.</FieldHelp>
                    </Field>
                    <Field>
                        <FieldLabel htmlFor="blGroup">Grupo</FieldLabel>
                        <Autocomplete
                            id="blGroup"
                            options={scope.groupOptions}
                            value={scope.groupOptions.find((option) => option.id === scope.groupId) ?? null}
                            onChange={this.emitGroupChanged}
                            getOptionLabel={(option) => option.name}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            size="small"
                            noOptionsText="Nenhum grupo encontrado"
                            renderInput={(params) => (
                                <TextField {...params} placeholder="Todos os grupos" sx={inputSx} />
                            )}
                            fullWidth
                        />
                    </Field>
                    <SwitchRow>
                        <Switch
                            size="small"
                            checked={scope.onlyExceeded}
                            onChange={this.emitOnlyExceededChanged}
                            sx={switchSx}
                            slotProps={{
                                input: { 'aria-label': 'Exibir somente com excedente' }
                            }}
                        />
                        <div>
                            <b>Exibir somente com excedente</b>
                            <FieldHelp>
                                As competências que ficaram dentro da franquia saem da grade, mas continuam somando nos
                                indicadores.
                            </FieldHelp>
                        </div>
                    </SwitchRow>
                </FilterBody>
                <ApplyBand>
                    <Button
                        sx={buttonSx()}
                        onClick={this.emitClear}
                        disabled={!scope.isFiltering}
                        startIcon={<CleaningServicesIcon />}
                    >
                        Limpar filtros
                    </Button>
                    <Button sx={buttonSx('primary')} onClick={this.emitApply}>
                        Aplicar
                    </Button>
                </ApplyBand>
            </>
        )
    }

    renderCloseFilter() {
        return (
            <CollapsedPaneHeader>
                <Tooltip title="Mostrar filtros">
                    <IconActionButton size="small" aria-label="Mostrar filtros" onClick={this.emitToggleExpanded}>
                        <KeyboardDoubleArrowLeftIcon />
                    </IconActionButton>
                </Tooltip>
                {this.scope.isFiltering && (
                    <Tooltip title="Limpar filtros">
                        <IconActionButton size="small" aria-label="Limpar filtros" onClick={this.emitClear}>
                            <CleaningServicesIcon />
                        </IconActionButton>
                    </Tooltip>
                )}
                <CollapsedPaneFilterIcon />
                <CollapsedPaneLabel>Filtros</CollapsedPaneLabel>
            </CollapsedPaneHeader>
        )
    }
}

export const BillingFilterPaneView = classToFComponent(FilterPaneViewClass, React)
export default BillingFilterPaneView
